import { APP_INITIALIZER, NgModule, Optional, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { environment } from 'environments/environment';
// OIDC (Azure AD / Entra ID company account login)
import { AuthConfig, OAuthModule, OAuthService } from 'angular-oauth2-oidc';
import { OAuthInterceptor } from './oauth/oauth.interceptor';
// NgRx
import { Store } from '@ngrx/store';
import * as authActions from 'app/auth/store/auth.actions';
import * as fromRoot from 'app/core/store';
import { SessionsInterceptor } from './sessions/sessions.interceptor';

// Implicit flow on purpose: Azure AD returns the id_token/access_token directly in the
// redirect fragment, so the browser never POSTs to the AAD token endpoint. Auth-code+PKCE
// would do that POST cross-origin, which AAD only allows for app registrations typed
// "Single-page application" (AADSTS9002326 otherwise). Our client app registrations are
// typed "Web" and we don't want to require an Azure Portal change per client.
export const azureAuthConfig: AuthConfig = {
  issuer: 'https://login.microsoftonline.com/common/v2.0',
  redirectUri: window.location.origin,
  postLogoutRedirectUri: window.location.origin,
  clientId: environment.clientId,
  responseType: 'id_token token',
  scope: `openid profile api://${environment.clientId}/access_as_user`,
  strictDiscoveryDocumentValidation: false,
  skipIssuerCheck: true, // 'common' is a multi-tenant placeholder; the id_token issuer is the caller's own tenant
  disableAtHashCheck: true, // AAD id_tokens don't populate at_hash the way the lib expects
  clockSkewInSec: 300,
  sessionChecksEnabled: false, // AAD doesn't support the OIDC check_session_iframe
};

// Completes the Azure AD redirect flow (if we're returning from one) and only then loads the
// session, in that order and in a single initializer. Splitting this into two independent
// APP_INITIALIZERs used to race: the session-load one dispatches synchronously and its effects
// (refreshAzureToken -> silentRefresh) call createLoginUrl(), which generates and overwrites the
// oauth nonce in sessionStorage, before this one had a chance to validate the nonce from the
// actual Azure redirect we just came back from -> AADSTS/'invalid_nonce_in_state'.
function initializeAppFactory(oauthService: OAuthService, store: Store<fromRoot.AppState>): () => Promise<void> {
  return () => {
    oauthService.configure(azureAuthConfig);
    return oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      if (oauthService.hasValidIdToken()) {
        oauthService.setupAutomaticSilentRefresh();
      }
      store.dispatch(authActions.loadFromSessionStorage());
    });
  };
}

@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    OAuthModule.forRoot()
  ],
  declarations: [],
  providers: [
  {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      multi: true,
      deps: [OAuthService, Store]
  },
  {
      provide: HTTP_INTERCEPTORS,
      useClass: SessionsInterceptor,
      multi: true
  },
  {
      provide: HTTP_INTERCEPTORS,
      useClass: OAuthInterceptor,
      multi: true
  }
  ],
  exports: []
})
export class AuthModule {
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: AuthModule
  ) {
    if (parentModule) {
      throw new Error('AuthModule is already loaded. Import only in CoreModule');
    }
  }
}
